import { ChecklistDiscussion } from '@/types/discussion';
import { MessageCircle, Edit2, Trash2, MoreVertical } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';

interface MessageItemProps {
  message: ChecklistDiscussion;
  currentUserId: number;
  onEdit?: (id: number, newMessage: string) => void;
  onDelete?: (id: number) => void;
}

export const MessageItem = ({ message, currentUserId, onEdit, onDelete }: MessageItemProps) => {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(message.message);
  const actionsRef = useRef<HTMLDivElement>(null);

  const isOwnMessage = message.user_id === currentUserId;
  const canEdit = isOwnMessage && !message.is_deleted;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowActions(false);
      }
    };

    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const handleSaveEdit = () => {
    if (editedMessage.trim() && editedMessage !== message.message) {
      onEdit?.(message.id, editedMessage);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedMessage(message.message);
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
        return 'bg-blue-100 text-blue-700';
      case 'owner':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const timestamp = format(new Date(message.created_at), 'dd MMM yyyy, HH:mm', { locale: localeId });

  const renderBubbleContent = () => {
    if (message.is_deleted) {
      return <p className="text-sm italic opacity-60">Pesan telah dihapus</p>;
    }

    if (isEditing) {
      return (
        <div className="space-y-2">
          <textarea
            value={editedMessage}
            onChange={e => setEditedMessage(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            rows={3}
            autoFocus
            placeholder="Edit pesan..."
            aria-label="Edit message"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSaveEdit}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Simpan
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Batal
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((url, index) => (
              <a
                key={index}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-blue-200 hover:underline"
              >
                <MessageCircle size={12} />
                Attachment {index + 1}
              </a>
            ))}
          </div>
        )}
      </>
    );
  };

  const bubbleContent = renderBubbleContent();

  if (isOwnMessage) {
    return (
      <div className="flex justify-end mb-2 px-2 md:px-4">
        <div className="max-w-[70%]">
          {/* Metadata row — right aligned */}
          <div className="flex items-center justify-end gap-2 mb-1">
            {message.is_edited && <span className="text-xs text-gray-400 dark:text-gray-500 italic">(edited)</span>}
            <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Anda</span>

            {/* Three-dot action menu */}
            {canEdit && !isEditing && (
              <div className="relative" ref={actionsRef}>
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  aria-label="Message actions"
                >
                  <MoreVertical size={14} className="text-gray-500" />
                </button>
                {showActions && (
                  <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-10">
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 flex items-center gap-2"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Hapus pesan ini?')) {
                          onDelete?.(message.id);
                        }
                        setShowActions(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 text-red-600"
                    >
                      <Trash2 size={14} />
                      Hapus
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Blue bubble */}
          <div className="bg-blue-600 text-white px-4 py-2 rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-sm">
            {bubbleContent}
          </div>
        </div>
      </div>
    );
  }

  // Other user — left side
  return (
    <div className="flex justify-start mb-2 px-2 md:px-4">
      <div className="max-w-[70%]">
        {/* Metadata row — left aligned */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{message.user_name}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(message.user_role)}`}>
            {message.user_role}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{timestamp}</span>
          {message.is_edited && <span className="text-xs text-gray-400 dark:text-gray-500 italic">(edited)</span>}
        </div>

        {/* Slate bubble */}
        <div className="bg-slate-700 dark:bg-slate-800 text-white px-4 py-2 rounded-tl-sm rounded-tr-2xl rounded-br-2xl rounded-bl-2xl">
          {bubbleContent}
        </div>
      </div>
    </div>
  );
};
